import React, { useMemo, useState } from 'react';
import { getLocale, useIntl } from 'umi';
import { ConfigProvider, Icon, Table } from 'tea-component';
import type { TableProps } from 'tea-component';
import type { SortBy } from 'tea-component/lib/table/addons';
import { getTeaLocale } from '@/pages/home/utils/lang';
import { NO_DATE } from '../../constants';
import type { PageInfoType } from '../../types/common';
import './index.scss';
import { buriedTableBtnPoint } from '@op/operationLog/buriedPoint';
import NoData, { NoDataType } from '../NoDataCommon';

const { pageable, sortable, scrollable, autotip } = Table.addons;
/**
 * @param records           数据
 * @param tableSticky       是否需要固定表头
 * @param pageInfo          分页信息，与接口同步
 * @param setPageInfo       设置分页信息
 * @param defaultPageInfo   默认分页信息
 * @param total             表格数据总数
 * @param sortColumns       需要添加排序的列
 * @param sortInfo          排序信息，一般由useState创建的数组
 * @param defaultSort       默认排序数据
 * @param pageSizeOptions   支持的页长设置
 * @param sortType          排序类型，local:本地排序 和 server：服务端排序，这将参数传出去
 * @param onRecordsChange   数据变化的时候的回调，仅支持sortType === local的情况
 * @param minWidth          最小宽度
 * @param hasPageable       是否有分页器
 * @param loading           loading状态
 * @param otherTableProps   其他表格的一些参数
 * @returns
 */
export default function TableCommonHook({
  records,
  tableSticky,
  total = 0,
  columns,
  sortColumns = [],
  pageSizeOptions = [10, 20, 30, 50, 100, 200],
  sortType = 'server',
  sortInfo,
  defaultSort = {
    by: 'mention',
    order: 'desc',
  },
  changeSort,
  pageInfo = { page: 1, page_size: 10 },
  setPageInfo,
  defaultPageInfo = { page: 1, page_size: 10 },
  onRecordsChange = () => { }, // 真实的数据列表
  minWidth = 1400,
  hasPageable = true,
  loading = false,
  logBtnId = '',
  maxHeight,
  addons: otherAddons = [],
  className,
  height = 506,
  loadingHeight,
  emptyHeight,
  emptyProps = {},
  loaclPageable = false,
  buriedTableBtn = buriedTableBtnPoint,
  tableRef,
  sortClearable = false,
  ...otherTableProps
}: {
  tableSticky?: boolean;
  pageInfo?: PageInfoType;
  setPageInfo?: (pageInfo: PageInfoType) => void;
  defaultPageInfo?: PageInfoType;
  total?: number;
  sortColumns?: Array<string>;
  pageSizeOptions?: Array<number>;
  defaultSort?: SortBy<any>;
  sortInfo?: [SortBy<any>, (item: SortBy<any>) => void];
  changeSort?: (item: SortBy<any>) => void;
  sortType?: 'server' | 'local';
  onRecordsChange?: (list: Array<any>) => void; // 真实的数据列表
  minWidth?: number | string;
  hasPageable?: boolean;
  loading?: boolean;
  logBtnId?: string;
  maxHeight?: number;
  addons?: Array<any>;
  className?: string;
  height?: number;
  loadingHeight?: number;
  emptyHeight?: number;
  emptyProps?: NoDataType;
  loaclPageable?: boolean;
  tableRef?: React.MutableRefObject<HTMLTableElement>;
  buriedTableBtn?: (
    commonId: string,
    key: string,
    extInfo?: {
      [key: string]: string | any;
    } | string) => void;
  sortClearable?: boolean;
} & TableProps<any>) {
  const { formatMessage } = useIntl();

  const localeSortBy = useState<SortBy<any>>(defaultSort);
  // 排序
  const [sort, setSort] = sortInfo || localeSortBy;

  const tableRecords = useMemo(() => {
    if (sortType === 'server') {
      return records;
    }

    if (!sort.by) {
      onRecordsChange?.(records || []);
      return records;
    }

    const newList = records?.sort(sortable.comparer([sort])) || [];
    onRecordsChange?.(newList);
    return newList;
  }, [records, sort]);

  const locale = getTeaLocale(getLocale());
  // 表格的loading和无数据有28的padding
  const emHeight = `${(emptyHeight || height) - 28}px`;
  const ldHeight = `${(loadingHeight || height) - 28}px`;

  const addons = [
    sortable({
      columns: sortColumns.map((item) => ({ prefer: 'desc', key: item })),
      value: [sort],
      onChange: (value) => {
        const { order, by } = value?.[0] || {};
        let newSort: SortBy<any> = defaultSort;

        if (!by) {
          if (sortClearable) {
            newSort = { by: '', order: undefined };
          } else {
            newSort = { ...sort, order: 'desc' };
          }
        } else if (by === sort.by) {
          newSort = { ...sort, order };
        } else if (by !== sort.by) {
          newSort = { order, by };
        }
        buriedTableBtn(logBtnId, 'Sort', newSort);
        setSort(newSort);
        changeSort?.(newSort);
      },
    }),
    scrollable({
      minWidth,
      maxHeight,
    }),
    autotip({
      isLoading: loading,
      hideIcon: true,
      loadingText: (
        <div className="dbo-table-common-center">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: ldHeight,
              lineHeight: ldHeight,
            }}
          >
            <Icon type="loading" style={{ marginRight: 5 }} />{' '}
            {formatMessage({ id: NO_DATE.LOAD })}
          </div>
        </div>
      ),
      emptyText: (
        <div className={`dbo-table-common-center  ${emptyProps.className}`}>
          <NoData
            hasImg
            hasTitle
            hasDesc
            style={{ height: emHeight }}
            {...emptyProps}
            className="-"
          ></NoData>
        </div>
      ),
    }),
    ...otherAddons,
  ];

  if (hasPageable) {
    addons.push(
      pageable({
        recordCount: total,
        pageIndex: pageInfo.page,
        pageSize: pageInfo.page_size,
        onPagingChange: (query) => {
          const pageInfo = {
            page: query.pageIndex || defaultPageInfo.page,
            page_size: query.pageSize || defaultPageInfo.page_size,
          };
          buriedTableBtn(logBtnId, 'Paging', pageInfo);
          setPageInfo?.(pageInfo);
        },
        pageSizeOptions,
      }),
    );
  } else if (loaclPageable) {
    addons.push(pageable());
  }

  return (
    <ConfigProvider locale={locale}>
      <Table
        verticalTop
        className={`dbo-tablecommon ${tableSticky ? 'dbo-table-sticky dbo-table-sticky--withfilter' : ''} ${className}`}
        {...otherTableProps}
        ref={tableRef}
        records={tableRecords || []}
        columns={columns}
        addons={addons}
      />
    </ConfigProvider>
  );
}
